import { stringifyQuery } from 'ufo';
import { defineNuxtPlugin } from '#imports';

import { validate } from '~/lib/validate-goliath-response';
import type { OperationVariables } from '@apollo/client';
import { start, finish } from '~/composables/use-loading';

export type GoliathApiClient = <V extends OperationVariables, R, K extends keyof NonNullable<R>>(
  document: string,
  variables: V,
  responseKey: K,
  progress?: boolean,
  skipCache?: boolean,
  signal?: AbortSignal | undefined
) => Promise<NonNullable<NonNullable<R>[K]> | undefined>;

// We declare $goliathApi for frontend use.
const buildApiClient =
  (cdnUrl?: string | undefined): GoliathApiClient =>
  async <V, R, K extends keyof NonNullable<R>>(
    operation: string,
    variables: V,
    responseKey: K,
    progress: boolean | undefined = undefined,
    skipCache = false,
    signal: AbortSignal | undefined = undefined
  ): Promise<NonNullable<NonNullable<R>[K]> | undefined> => {
    const contexts = {
      operation: { operation, isClient: true },
      params: { params: JSON.stringify(variables) },
    };
    const config: Record<string, any> = {}; // RequestConfig
    if (skipCache) {
      config.headers = {
        'x-use-cache': false,
      };
    }

    try {
      if (progress) {
        start();
      }

      let response: R;
      // if the query is too lang, we have to switch to post.
      if (stringifyQuery(variables as Record<any, any>).length > 4000) {
        // Post cannot be cached in CloudFront.
        response = (await $fetch(`/api/goliath/${operation}`, {
          method: 'POST',
          body: variables as OperationVariables,
          signal,
        })) as R;
      } else {
        response = (await $fetch(`${cdnUrl ?? ''}/api/goliath/${operation}`, {
          method: 'GET',
          query: variables as OperationVariables,
          signal,
        })) as R;
      }

      return validate<R, K>(response, responseKey, contexts);
    } catch (e: Error | any) {
      const status = e.status || e.statusCode || e.response?.status;
      // Check, if the error was already send to sentry in brontend.
      if (status !== 404 && !e.data?.data?.isHandledBySentry) {
        captureException(e, { captureContext: { contexts } });
      }

      throw e;
    } finally {
      if (progress) {
        finish();
      }
    }
  };

export default defineNuxtPlugin((nuxtApp) => {
  const goliathApi = buildApiClient();
  return {
    provide: {
      goliathApi,
    },
  };
});
